import { useEffect, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  message,
  Row,
  Col,
  Statistic,
  Tabs,
  Upload,
  Alert,
  Switch,
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
  SyncOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { UploadProps } from 'antd/es/upload'
import dayjs from 'dayjs'
import { confirmDelete, confirmDanger } from '../../hooks/useConfirm'
import { backupAPI } from '../../api'

const { Option } = Select
const { TextArea } = Input

interface BackupRecord {
  id: number
  fileName: string
  fileSize: number
  backupType: 'full' | 'incremental'
  backupMethod: 'auto' | 'manual'
  status: 'success' | 'failed' | 'in_progress'
  tables: number
  rows: number
  duration: number
  storagePath: string
  remark: string
  createdAt: string
  operator: string
}

interface BackupConfig {
  autoBackup: boolean
  backupTime: string
  backupType: 'full' | 'incremental'
  retentionDays: number
  compressBackup: boolean
  backupTables: string[]
  storagePath: string
}

const DataBackup = () => {
  const [activeTab, setActiveTab] = useState('records')
  const [loading, setLoading] = useState(false)
  const [backingUp, setBackingUp] = useState(false)
  const [restoring, setRestoring] = useState(false)

  // 备份记录
  const [records, setRecords] = useState<BackupRecord[]>([])
  const [selectedRecord, setSelectedRecord] = useState<BackupRecord | null>(null)
  const [restoreModalVisible, setRestoreModalVisible] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })

  // 备份配置
  const [config, setConfig] = useState<BackupConfig>({
    autoBackup: true,
    backupTime: '02:00',
    backupType: 'full',
    retentionDays: 30,
    compressBackup: true,
    backupTables: ['all'],
    storagePath: '/data/backups/',
  })
  const [configForm] = Form.useForm()
  const [configSaving, setConfigSaving] = useState(false)

  // 手动备份
  const [backupModalVisible, setBackupModalVisible] = useState(false)
  const [backupForm] = Form.useForm()

  // 数据库统计
  const [dbStats, setDbStats] = useState({
    totalSize: 0,
    tableCount: 0,
    totalRows: 0,
    lastBackupTime: '',
    lastBackupSize: 0,
    backupCount: 0,
    totalBackupSize: 0,
  })

  // 可备份的数据表
  const [tables, setTables] = useState<{ name: string; rows: number; size: number }[]>([])

  useEffect(() => {
    fetchBackupRecords()
    fetchDbStats()
    fetchTables()
    fetchConfig()
  }, [])

  useEffect(() => {
    fetchBackupRecords()
  }, [pagination.current, pagination.pageSize])

  const fetchBackupRecords = async () => {
    setLoading(true)
    try {
      const params = {
        page: pagination.current,
        pageSize: pagination.pageSize,
      }
      const res = await backupAPI.getRecords(params)
      if (res.code === 200) {
        setRecords(res.data.list || [])
        setPagination(prev => ({ ...prev, total: res.data.total || 0 }))
      }
    } catch (error) {
      // 错误由 request 拦截器统一处理
    } finally {
      setLoading(false)
    }
  }

  const fetchDbStats = async () => {
    try {
      const res = await backupAPI.getStats()
      if (res.code === 200) {
        setDbStats(res.data)
      }
    } catch (error) {
      // 错误统一处理
    }
  }

  const fetchTables = async () => {
    // 模拟数据表
    setTables([
      { name: 'users', rows: 1250, size: 2.5 },
      { name: 'products', rows: 356, size: 1.8 },
      { name: 'orders', rows: 5680, size: 8.2 },
      { name: 'order_items', rows: 12450, size: 6.5 },
      { name: 'members', rows: 890, size: 1.2 },
      { name: 'coupons', rows: 45, size: 0.3 },
      { name: 'stores', rows: 28, size: 0.2 },
      { name: 'delivery_areas', rows: 15, size: 0.1 },
      { name: 'operation_logs', rows: 2350, size: 3.5 },
      { name: 'settings', rows: 12, size: 0.1 },
    ])
  }

  const fetchConfig = async () => {
    try {
      const res = await backupAPI.getConfig()
      if (res.code === 200 && res.data) {
        setConfig(res.data)
        configForm.setFieldsValue(res.data)
      }
    } catch (error) {
      // 错误统一处理
    }
  }

  const handleManualBackup = () => {
    backupForm.resetFields()
    backupForm.setFieldsValue({ backupType: 'full', tables: ['all'] })
    setBackupModalVisible(true)
  }

  const handleBackupSubmit = async () => {
    try {
      const values = await backupForm.validateFields()

      confirmDanger({
        title: '确认手动备份',
        content: '确定要立即执行备份吗？备份过程中可能会影响系统性能。',
        okText: '开始备份',
        okType: 'primary',
        module: '数据备份',
        action: '手动备份',
        targetName: values.backupType === 'full' ? '全量备份' : '增量备份',
        onOk: async () => {
          setBackingUp(true)
          try {
            const res = await backupAPI.create(values)
            if (res.code === 200) {
              message.success('备份任务已启动')
              setBackupModalVisible(false)
              fetchBackupRecords()
            }
          } finally {
            setBackingUp(false)
          }
        },
      })
    } catch (error) {
      // 表单验证错误
    }
  }

  const handleRestore = (record: BackupRecord) => {
    setSelectedRecord(record)
    setRestoreModalVisible(true)
  }

  const handleRestoreConfirm = async () => {
    if (!selectedRecord) return

    confirmDanger({
      title: '确认数据恢复',
      content: `您确定要恢复备份「${selectedRecord.fileName}」吗？恢复操作将覆盖当前数据库中的所有数据，此操作不可逆！`,
      okText: '确认恢复',
      okType: 'danger',
      module: '数据备份',
      action: '数据恢复',
      targetName: selectedRecord.fileName,
      onOk: async () => {
        setRestoring(true)
        try {
          await backupAPI.restore(selectedRecord.id)
          message.success('数据恢复成功')
          setRestoreModalVisible(false)
        } finally {
          setRestoring(false)
        }
      },
    })
  }

  const handleDownload = async (record: BackupRecord) => {
    try {
      const res = await backupAPI.download(record.id)
      const blob = new Blob([res.data], { type: 'application/gzip' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = record.fileName
      a.click()
      window.URL.revokeObjectURL(url)
      message.success('开始下载')
    } catch (error) {
      message.error('下载失败')
    }
  }

  const handleDelete = (record: BackupRecord) => {
    confirmDelete(record.fileName, '数据备份', async () => {
      const res = await backupAPI.delete(record.id)
      if (res.code === 200) {
        setRecords(records.filter(r => r.id !== record.id))
        message.success('删除成功')
        fetchBackupRecords()
      }
    }, '删除后备份文件将无法恢复。')
  }

  const handleSaveConfig = async () => {
    try {
      const values = await configForm.validateFields()
      setConfigSaving(true)
      const res = await backupAPI.saveConfig(values)
      if (res.code === 200) {
        setConfig(values)
        message.success('配置保存成功')
      }
    } catch (error) {
      message.error('保存失败')
    } finally {
      setConfigSaving(false)
    }
  }

  const handleUploadRestore: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options
    const fileObj = file as File

    confirmDanger({
      title: '确认从备份文件恢复',
      content: `您确定要使用上传的文件「${fileObj.name}」恢复数据吗？此操作将覆盖当前数据库，不可逆！`,
      okText: '确认恢复',
      okType: 'danger',
      module: '数据备份',
      action: '上传恢复',
      targetName: fileObj.name,
      onOk: async () => {
        setRestoring(true)
        try {
          const res = await backupAPI.uploadRestore(file as File)
          if (res.code === 200) {
            message.success('备份文件上传并恢复成功')
            onSuccess?.({})
            fetchBackupRecords()
          } else {
            throw new Error(res.message || '恢复失败')
          }
        } catch (error) {
          onError?.(error as Error)
          message.error('恢复失败')
        } finally {
          setRestoring(false)
        }
      },
    })
  }

  const formatFileSize = (size: number) => {
    if (size < 1) return `${(size * 1024).toFixed(2)} MB`
    if (size < 1024) return `${size.toFixed(2)} GB`
    return `${(size / 1024).toFixed(2)} TB`
  }

  const columns: ColumnsType<BackupRecord> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: '文件名',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 280,
      ellipsis: true,
    },
    {
      title: '大小',
      dataIndex: 'fileSize',
      key: 'fileSize',
      width: 100,
      render: (val: number) => formatFileSize(val),
    },
    {
      title: '类型',
      dataIndex: 'backupType',
      key: 'backupType',
      width: 90,
      render: (val: string) => (
        <Tag color={val === 'full' ? 'blue' : 'green'}>
          {val === 'full' ? '全量' : '增量'}
        </Tag>
      ),
    },
    {
      title: '方式',
      dataIndex: 'backupMethod',
      key: 'backupMethod',
      width: 80,
      render: (val: string) => (
        <Tag color={val === 'auto' ? 'default' : 'orange'}>
          {val === 'auto' ? '自动' : '手动'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (val: string) => {
        const statusMap: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
          success: { color: 'green', icon: <CheckCircleOutlined />, text: '成功' },
          failed: { color: 'red', icon: <ExclamationCircleOutlined />, text: '失败' },
          in_progress: { color: 'blue', icon: <SyncOutlined spin />, text: '进行中' },
        }
        return (
          <Tag color={statusMap[val]?.color} icon={statusMap[val]?.icon}>
            {statusMap[val]?.text}
          </Tag>
        )
      },
    },
    {
      title: '耗时',
      dataIndex: 'duration',
      key: 'duration',
      width: 80,
      render: (val: number) => `${val}秒`,
    },
    {
      title: '操作人',
      dataIndex: 'operator',
      key: 'operator',
      width: 100,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (val: string) => val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: BackupRecord) => (
        <Space size="small">
          <Button type="link" size="small" icon={<SyncOutlined />} onClick={() => handleRestore(record)}>
            恢复
          </Button>
          <Button type="link" size="small" icon={<DownloadOutlined />} onClick={() => handleDownload(record)}>
            下载
          </Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div className="data-backup-page">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="数据库大小"
              value={dbStats.totalSize}
              precision={2}
              suffix="GB"
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="数据表数量"
              value={dbStats.tableCount}
              suffix="张"
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="总记录数"
              value={dbStats.totalRows}
              suffix="条"
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="备份总数"
              value={dbStats.backupCount}
              suffix="个"
              prefix={<HistoryOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="备份总大小"
              value={dbStats.totalBackupSize}
              precision={2}
              suffix="GB"
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="最近备份"
              value={dbStats.lastBackupTime ? dayjs(dbStats.lastBackupTime).format('MM-DD HH:mm') : '-'}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="备份记录" key="records">
            <Space wrap style={{ marginBottom: 16 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleManualBackup}>
                手动备份
              </Button>
              <Upload
                customRequest={handleUploadRestore}
                showUploadList={false}
                accept=".sql,.gz,.zip"
              >
                <Button icon={<UploadOutlined />}>
                  上传备份恢复
                </Button>
              </Upload>
            </Space>

            <Table
              columns={columns}
              dataSource={records}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1400 }}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条`,
                onChange: (page, pageSize) => setPagination({ ...pagination, current: page, pageSize }),
              }}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="备份配置" key="config">
            <Row gutter={24}>
              <Col span={16}>
                <Card title="自动备份设置" size="small">
                  <Form form={configForm} layout="vertical">
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="autoBackup" label="自动备份" valuePropName="checked">
                          <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="backupTime" label="备份时间">
                          <Select style={{ width: '100%' }}>
                            <Option value="00:00">00:00</Option>
                            <Option value="01:00">01:00</Option>
                            <Option value="02:00">02:00</Option>
                            <Option value="03:00">03:00</Option>
                            <Option value="04:00">04:00</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="backupType" label="备份类型">
                          <Select style={{ width: '100%' }}>
                            <Option value="full">全量备份</Option>
                            <Option value="incremental">增量备份</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="retentionDays" label="保留天数">
                          <Input type="number" addonAfter="天" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="compressBackup" label="压缩备份" valuePropName="checked">
                          <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="storagePath" label="存储路径">
                          <Input placeholder="/data/backups/" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item>
                      <Button type="primary" onClick={handleSaveConfig} loading={configSaving}>
                        保存配置
                      </Button>
                    </Form.Item>
                  </Form>
                </Card>
              </Col>
              <Col span={8}>
                <Card title="数据表列表" size="small">
                  <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                    {tables.map(table => (
                      <div key={table.name} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '8px 0',
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <span>
                          {table.name}
                          <span style={{ marginLeft: 8, fontSize: 12, color: '#999' }}>
                            ({table.rows.toLocaleString()} 行)
                          </span>
                        </span>
                        <span style={{ color: '#666' }}>{formatFileSize(table.size)}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </Col>
            </Row>
          </Tabs.TabPane>
        </Tabs>
      </Card>

      {/* 手动备份弹窗 */}
      <Modal
        title="手动备份"
        open={backupModalVisible}
        onOk={handleBackupSubmit}
        onCancel={() => setBackupModalVisible(false)}
        confirmLoading={backingUp}
        width={500}
        destroyOnClose
      >
        <Alert
          message="手动备份将立即执行，备份过程中可能会影响系统性能"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={backupForm} layout="vertical">
          <Form.Item name="backupType" label="备份类型" initialValue="full" rules={[{ required: true, message: '请选择备份类型' }]}>
            <Select>
              <Option value="full">全量备份</Option>
              <Option value="incremental">增量备份</Option>
            </Select>
          </Form.Item>
          <Form.Item name="tables" label="选择数据表" initialValue={['all']}>
            <Select mode="multiple" placeholder="默认备份全部数据表">
              <Option value="all">全部数据表</Option>
              {tables.map(t => (
                <Option key={t.name} value={t.name}>
                  {t.name} ({formatFileSize(t.size)})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <TextArea rows={3} placeholder="请输入备份备注（选填）" maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>

      {/* 恢复备份确认弹窗 */}
      <Modal
        title="确认恢复"
        open={restoreModalVisible}
        onOk={handleRestoreConfirm}
        onCancel={() => setRestoreModalVisible(false)}
        confirmLoading={restoring}
        okText="确认恢复"
        okType="danger"
        width={500}
      >
        <Alert
          message="警告"
          description={
            <div>
              <p>您确定要恢复以下备份吗？</p>
              <p><strong>文件名：</strong>{selectedRecord?.fileName}</p>
              <p><strong>备份时间：</strong>{selectedRecord?.createdAt ? dayjs(selectedRecord.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}</p>
              <p><strong>文件大小：</strong>{selectedRecord ? formatFileSize(selectedRecord.fileSize) : ''}</p>
              <p style={{ color: '#cf1322', marginTop: 16 }}>
                注意：恢复操作将覆盖当前数据库中的所有数据，此操作不可逆！
              </p>
            </div>
          }
          type="error"
          showIcon
        />
      </Modal>
    </div>
  )
}

export default DataBackup
